import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";

import { Stack } from "@mantine/core";

import { Loading } from "@/components/Loading";
import { AnswerItem } from "@/encounters/components/AnswerItem";
import {
  BodyHeightUnitToCode,
  generateVitalSignsQuestionnaireResponse,
} from "@/encounters/components/VitalSigns/zod-schemas/vital-sign";
import { convertInchesToFeetAndInches } from "@/utils/ucum_units";
import { formatQuestionnaireResponseItemAnswer } from "@/utils/zod-questionnaires";

import { VITAL_SIGN_FIELD_NAME_TO_LABEL } from "../VitalSignsFormSection";

type VitalSignsFormReviewWrapperProps = {
  questionnaireResponse?: QuestionnaireResponse;
};

export function VitalSignsFormReviewWrapper({
  questionnaireResponse,
}: VitalSignsFormReviewWrapperProps) {
  if (!questionnaireResponse) {
    return <Loading />;
  }
  const questionnaireAnswers = getQuestionnaireAnswers(questionnaireResponse);
  const formattedQuestionnaireResponse =
    generateVitalSignsQuestionnaireResponse(questionnaireAnswers);
  const formattedAnswers = getQuestionnaireAnswers(formattedQuestionnaireResponse);

  function getAnswer(fieldName: string) {
    const answerKey = Object.keys(formattedAnswers).find((aKey) => aKey === `${fieldName}-value`);
    return answerKey ? formattedAnswers[answerKey] : undefined;
  }

  function getFormattedValue(fieldName: string): string {
    if (fieldName === "bloodPressure") {
      const systolicBPAnswer = getAnswer("systolicBP");
      const diastolicBPAnswer = getAnswer("diastolicBP");
      if (!systolicBPAnswer || !diastolicBPAnswer) {
        return "-";
      }
      return `${formatQuestionnaireResponseItemAnswer(systolicBPAnswer)}/${formatQuestionnaireResponseItemAnswer(diastolicBPAnswer)}`;
    }

    const answer = getAnswer(fieldName);

    if (!answer) {
      return "-";
    }

    if (fieldName === "height" && answer?.valueQuantity?.code === BodyHeightUnitToCode.in) {
      const { feet, inches } = convertInchesToFeetAndInches(answer?.valueQuantity?.value ?? 0);
      return `${feet}' ${inches}"`;
    }

    if (fieldName === "temperature") {
      const temperatureMethodAnswer = getAnswer("temperatureMethod");
      if (temperatureMethodAnswer) {
        return `${formatQuestionnaireResponseItemAnswer(answer)} / ${formatQuestionnaireResponseItemAnswer(temperatureMethodAnswer)}`;
      }
    }

    return formatQuestionnaireResponseItemAnswer(answer);
  }

  return (
    <Stack
      p="md"
      bg="white"
      justify="space-between"
      style={{ borderRadius: 8 }}
      bd="1px solid var(--mantine-color-cream-4)"
    >
      {Array.from(VITAL_SIGN_FIELD_NAME_TO_LABEL, ([fieldName, label]) => (
        <AnswerItem label={label} key={fieldName} value={getFormattedValue(fieldName)} />
      ))}
    </Stack>
  );
}
